import { Routes } from '@angular/router';
import { requireRole, SUPER_ADMIN_ROLE } from '@sisoma/auth';

import { Shell } from './core/layout/shell';

/**
 * All feature pages are lazy-loaded children of the shell, so the sidenav and
 * main-area frame persist across navigation.
 *
 * Adding a section = add a child route here + an entry in
 * `core/navigation/nav-items.ts`.
 */
export const routes: Routes = [
  // The 403 page must sit OUTSIDE the role-guarded shell and be matched BEFORE
  // it — otherwise the guard's redirect here would re-trigger the guard (or
  // fall through the shell's `**`) and loop. Rendered standalone, without the
  // sidenav frame.
  {
    path: 'forbidden',
    title: 'Not authorised',
    loadComponent: () =>
      import('./features/not-authorised/not-authorised').then((m) => m.NotAuthorised),
  },
  {
    path: '',
    component: Shell,
    // Coarse gate for the whole surface (fp1-04): logged-out → shared login;
    // logged-in without the superAdmin role → `/forbidden`. Admin is role-gated
    // rather than capability-gated, so there are no per-feature
    // `requireCapabilities(...)` guards on the child routes.
    canActivate: [requireRole(SUPER_ADMIN_ROLE)],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      {
        path: 'dashboard',
        title: 'Dashboard',
        loadComponent: () => import('./features/dashboard/dashboard').then((m) => m.Dashboard),
      },
      {
        path: 'settings',
        title: 'Settings',
        data: { pageTitle: 'Settings' },
        loadComponent: () =>
          import('./features/placeholder/placeholder-page').then((m) => m.PlaceholderPage),
      },
      {
        path: 'partner-areas',
        title: 'Partner areas',
        loadComponent: () =>
          import('./features/partner-areas/partner-areas').then((m) => m.PartnerAreas),
      },
      // SSO config is per-partner (keyed by `:creatorId`). The bare path lands on
      // a manual-entry field that navigates to the id route; a drill-in link
      // from the partner-areas list arrives with fp1-11. Both bind `creatorId`
      // via `withComponentInputBinding`.
      {
        path: 'sso-config',
        title: 'SSO configuration',
        loadComponent: () => import('./features/sso-config/sso-config').then((m) => m.SsoConfig),
      },
      {
        path: 'sso-config/:creatorId',
        title: 'SSO configuration',
        loadComponent: () => import('./features/sso-config/sso-config').then((m) => m.SsoConfig),
      },
      {
        path: '**',
        title: 'Page not found',
        loadComponent: () => import('./features/not-found/not-found').then((m) => m.NotFound),
      },
    ],
  },
];
